import React, { useEffect, useMemo, useRef, useState } from 'react';
import { C, TRIG_W, TRIG_H, nx, ny } from '../flowCanvas.constants';

const TYPE_COLORS = {
  start: '#22d3ee', end: '#f43f5e', viewer: C.purple, action: '#fb923c',
};

const normCmd = (v) => (typeof v === 'string' ? v.trim().toUpperCase() : '');
const toTitleCase = (v) => String(v || '')
  .toLowerCase()
  .replace(/\b\w/g, (m) => m.toUpperCase());

// ─── Hashtag / trigger node ────────────────────────────────────────────────────
export default function TriggerNode({
  step,
  selected,
  onClick,
  centreY,
  overrideX,
  overrideY,
  editable = false,
  onCommitHashtags,
  focusNonce = 0,
  onDraftHeightChange,
}) {
  const x    = overrideX ?? nx(step);
  const y    = overrideY ?? ny(step, centreY);
  const left = x - TRIG_W / 2;
  const top  = y - TRIG_H / 2;

  const config   = step.triggerConfig || {};
  const triggers = step.configuredTriggers || [];
  const isRequired = !!config.isRequired;
  const accent     = TYPE_COLORS[config.triggerType] || C.purple;

  const linkedCommands = Array.isArray(config.linkedCommands) ? config.linkedCommands.filter(Boolean) : [];
  const primaryCommand = linkedCommands[0] || triggers?.[0]?.command || null;
  const normalizedPrimaryCommand = normCmd(primaryCommand);

  const allHashtags = triggers.flatMap(t => (t.hashtags || []).map(h => (typeof h === 'string' ? h : h?.hashtag)).filter(Boolean));
  // Collect hashtags from ALL trigger entries for the primary command and deduplicate.
  // There may be multiple entries if the campaign has both a legacy template-id record
  // and a newer instance-id record for the same command.
  const primaryHashtags = useMemo(() => {
    if (!normalizedPrimaryCommand) return [];
    const all = triggers
      .filter(tt => normCmd(tt.command) === normalizedPrimaryCommand)
      .flatMap(tt => (tt.hashtags || []).map(h => (typeof h === 'string' ? h : h?.hashtag)).filter(Boolean));
    return Array.from(new Set(all));
  }, [normalizedPrimaryCommand, triggers]);

  // Multi-row editor state: one input per hashtag.
  const [draftTags, setDraftTags] = useState(() => (primaryHashtags.length ? [...primaryHashtags] : ['']));
  const inputRefs = useRef([]);
  const lastFocusNonceRef = useRef(0);
  const skipNextBlurCommitRef = useRef(false);

  let statusLabel, statusColor;
  if (allHashtags.length > 0) {
    statusLabel = allHashtags.slice(0, 2).join('  ') + (allHashtags.length > 2 ? `  +${allHashtags.length - 2}` : '');
    statusColor = C.purple;
  } else if (triggers.length > 0) {
    statusLabel = `${triggers.length} configured`;
    statusColor = C.purple;
  } else if (isRequired) {
    statusLabel = 'Required';
    statusColor = C.orange;
  } else {
    statusLabel = 'Optional';
    statusColor = C.textDim;
  }

  const title = step.stepTitle || '';
  const trunc = (s, n) => s.length > n ? s.slice(0, n) + '…' : s;

  const isHashtagNode = /hashtag/i.test(step.stepTitle || '') || /hashtag/i.test(step.stepDescription || '');
  const showInlineEditor = !!editable && isHashtagNode && config.triggerType === 'viewer' && !!primaryCommand && typeof onCommitHashtags === 'function';
  const hashtagHeaderH = isHashtagNode ? 16 : 0;

  // Dynamic height: grows with the number of draft tag rows when the inline editor is open.
  // Each row is 22px tall + 6px gap; header = 22px, bottom padding = 10px.
  // top stays fixed so the node grows downward without shifting connections above it.
  const tagCount = (showInlineEditor && draftTags.length > 0) ? draftTags.length : 1;
  const hashtagBaseH = isHashtagNode ? 92 : TRIG_H;
  const nodeH = showInlineEditor
    ? Math.max(hashtagBaseH, tagCount * 28 + 44 + hashtagHeaderH)
    : hashtagBaseH;
  const titleBandH = isHashtagNode ? 34 : 0;
  const slotX = left + 10;
  const slotY = top + 30 + hashtagHeaderH;
  const slotW = TRIG_W - 20;
  const slotH = Math.max(34, nodeH - (slotY - top) - 10);
  const showHashtagSlot = isHashtagNode;
  const displayTitle = showHashtagSlot
    ? toTitleCase(String(title || '').replace(/^#+\s*/, ''))
    : title;
  const borderCol = selected ? C.purple : C.border;

  // Report height changes to FlowCanvas so layout reflows immediately on local edits.
  useEffect(() => {
    onDraftHeightChange?.(nodeH);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeH]);

  // Keep draft state in sync with server refreshes.
  // Use a stable content key so the effect only fires when the actual hashtag
  // VALUES change (not just when the array reference changes due to re-render).
  // This prevents wiping user input mid-edit on every flow re-render.
  const primaryHashtagsKey = primaryHashtags.slice().sort().join(',');
  useEffect(() => {
    if (!showInlineEditor) return;
    setDraftTags(primaryHashtags.length ? [...primaryHashtags] : ['']);
    // NOTE: do NOT reset inputRefs.current here — the ref callbacks repopulate
    // automatically on each render, and clearing can cause stale blur events.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryHashtagsKey, showInlineEditor]);

  const normalizeTag = (v) => {
    const s = (v || '').trim();
    if (!s) return '';
    return s.startsWith('#') ? s : `#${s}`;
  };

  const getNormalizedUnique = (arr) => {
    const next = (arr || []).map(normalizeTag).filter(Boolean);
    return Array.from(new Set(next));
  };

  const sameSet = (a, b) => {
    const aa = getNormalizedUnique(a);
    const bb = getNormalizedUnique(b);
    if (aa.length !== bb.length) return false;
    return aa.every(v => bb.includes(v));
  };

  const commitTags = (nextDraft) => {
    if (!showInlineEditor) return;
    const nextHashtags = getNormalizedUnique(nextDraft);

    // No-op if nothing actually changed.
    if (sameSet(nextHashtags, primaryHashtags)) return;

    onCommitHashtags({
      step,
      command: primaryCommand,
      nextHashtags,
      currentHashtags: primaryHashtags,
    });
  };

  const addRowAndFocus = () => {
    if (!showInlineEditor) return;
    setDraftTags((cur) => {
      const next = [...cur];
      // If the last row is already empty, just focus it.
      const lastIdx = next.length - 1;
      if (lastIdx >= 0 && !String(next[lastIdx] || '').trim()) {
        setTimeout(() => inputRefs.current[lastIdx]?.focus?.(), 0);
        return next;
      }
      next.push('');
      setTimeout(() => {
        const idx = next.length - 1;
        inputRefs.current[idx]?.focus?.();
      }, 0);
      return next;
    });
  };

  useEffect(() => {
    if (!showInlineEditor) return;
    if (!focusNonce) return;
    if (focusNonce === lastFocusNonceRef.current) return;
    lastFocusNonceRef.current = focusNonce;

    // Treat focusNonce as an "add a new hashtag row" signal.
    addRowAndFocus();
  }, [focusNonce, showInlineEditor]);

  return (
    <g className="fc-node fc-node--trigger" onClick={onClick} style={{ cursor: 'pointer' }}>
      <rect x={left} y={top} width={TRIG_W} height={nodeH} rx="6"
        fill={selected ? C.cardSel : C.card}
        stroke={borderCol}
        strokeWidth={selected ? 2 : 1.5}
      />

      {showHashtagSlot ? (
        <>
          <clipPath id={`trig-hdr-${step.stepId}`}>
            <rect x={left} y={top} width={TRIG_W} height={titleBandH} rx="6" />
          </clipPath>
          <rect
            x={left}
            y={top}
            width={TRIG_W}
            height={titleBandH + 6}
            clipPath={`url(#trig-hdr-${step.stepId})`}
            fill="rgba(145,70,255,0.20)"
            stroke="none"
            style={{ pointerEvents: 'none' }}
          />
          <text x={left + 14} y={top + 23}
            fontSize="17" fill={C.purple}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>
            #
          </text>
          <text x={left + 36} y={top + 23}
            fontSize="11.5" fontWeight="700" fill={C.text}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>
            {trunc(displayTitle, 22)}
          </text>
        </>
      ) : (
        <text x={left + 12} y={top + 22}
          fontSize="10" fontWeight="700" fill={accent}
          style={{ pointerEvents: 'none', userSelect: 'none' }}>
          {trunc(displayTitle, 26)}
        </text>
      )}

      {!showInlineEditor && !showHashtagSlot ? (
        <>
          <rect x={left + 10} y={top + nodeH - 23}
            width={Math.min(statusLabel.length * 6.2 + 14, TRIG_W - 20)} height="16" rx="8"
            fill={allHashtags.length > 0 ? '#0d1f18' : isRequired ? '#2a0a04' : '#111526'}
          />
          <text x={left + 16} y={top + nodeH - 12}
            fontSize="8" fill={statusColor}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>
            {trunc(statusLabel, 30)}
          </text>
        </>
      ) : showHashtagSlot ? (
        <>
          <rect x={slotX} y={slotY}
            width={slotW} height={slotH} rx="5"
            fill={allHashtags.length > 0 ? 'rgba(145,70,255,0.09)' : 'none'}
            stroke={allHashtags.length > 0 ? 'rgba(145,70,255,0.28)' : C.textDim}
            strokeWidth="1"
            strokeDasharray={allHashtags.length > 0 ? undefined : '4 3'}
          />

          {!showInlineEditor ? (
            allHashtags.length > 0 ? (
              <g style={{ pointerEvents: 'none' }}>
                <text x={slotX + 10} y={slotY + 19}
                  fontSize="10.5" fontWeight="600" fill={C.text}
                  style={{ userSelect: 'none' }}>
                  {trunc(allHashtags.join('  '), 24)}
                </text>
              </g>
            ) : (
              <g style={{ pointerEvents: 'none' }}>
                <text x={slotX + slotW / 2} y={slotY + slotH / 2 - 4}
                  textAnchor="middle" fontSize="16" fill={C.textDim}
                  style={{ userSelect: 'none' }}>+</text>
                <text x={slotX + slotW / 2} y={slotY + slotH / 2 + 11}
                  textAnchor="middle" fontSize="9.5" fill={C.textDim}
                  style={{ userSelect: 'none' }}>Click to add hashtag</text>
              </g>
            )
          ) : (
            <foreignObject
              x={slotX + 8}
              y={slotY + 6}
              width={slotW - 16}
              height={slotH - 12}
              style={{ overflow: 'visible' }}
            >
              <div
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  width: '100%',
                }}
              >
                {draftTags.map((val, idx) => (
                  <div key={`${step.stepId}--row-${idx}`} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input
                      ref={(el) => { inputRefs.current[idx] = el; }}
                      value={val}
                      placeholder="hashtag"
                      onChange={(e) => {
                        const next = [...draftTags];
                        next[idx] = e.target.value;
                        setDraftTags(next);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          skipNextBlurCommitRef.current = true;
                          commitTags(draftTags);
                          inputRefs.current[idx]?.blur?.();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          e.stopPropagation();
                          skipNextBlurCommitRef.current = true;
                          setDraftTags(primaryHashtags.length ? [...primaryHashtags] : ['']);
                          inputRefs.current[idx]?.blur?.();
                        }
                      }}
                      onBlur={() => {
                        if (skipNextBlurCommitRef.current) {
                          skipNextBlurCommitRef.current = false;
                          return;
                        }
                        commitTags(draftTags);
                      }}
                      style={{
                        flex: 1,
                        height: '22px',
                        boxSizing: 'border-box',
                        borderRadius: '4px',
                        border: `1px solid ${accent}`,
                        background: 'rgba(0,0,0,0.35)',
                        color: C.text,
                        fontFamily: 'PrimaryFont, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
                        fontSize: '11px',
                        letterSpacing: '0.02em',
                        padding: '0 10px',
                        outline: 'none',
                      }}
                    />
                    <button
                      type="button"
                      aria-label="Delete hashtag"
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      onClick={() => {
                        const next = draftTags.filter((_, i) => i !== idx);
                        setDraftTags(next.length ? next : ['']);
                        commitTags(next.length ? next : ['']);
                      }}
                      style={{
                        width: '22px',
                        height: '22px',
                        flexShrink: 0,
                        borderRadius: '999px',
                        border: 'none',
                        background: '#dc2626',
                        color: '#fee2e2',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <svg width="11" height="12" viewBox="-6 -6 12 12.5" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
                        <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
                        <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
                        <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                        <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </foreignObject>
          )}
        </>
      ) : (
        <foreignObject
          x={left + 10}
          y={top + 22}
          width={TRIG_W - 20}
          height={nodeH - 30}
          style={{ overflow: 'visible' }}
        >
          <div
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              width: '100%',
            }}
          >
            {draftTags.map((val, idx) => (
              <div key={`${step.stepId}--row-${idx}`} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <input
                  ref={(el) => { inputRefs.current[idx] = el; }}
                  value={val}
                  placeholder={idx === 0 ? '#tag' : '#tag'}
                  onChange={(e) => {
                    const next = [...draftTags];
                    next[idx] = e.target.value;
                    setDraftTags(next);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      skipNextBlurCommitRef.current = true;
                      commitTags(draftTags);
                      inputRefs.current[idx]?.blur?.();
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      e.stopPropagation();
                      skipNextBlurCommitRef.current = true;
                      setDraftTags(primaryHashtags.length ? [...primaryHashtags] : ['']);
                      inputRefs.current[idx]?.blur?.();
                    }
                  }}
                  onBlur={() => {
                    if (skipNextBlurCommitRef.current) {
                      skipNextBlurCommitRef.current = false;
                      return;
                    }
                    commitTags(draftTags);
                  }}
                  style={{
                    flex: 1,
                    height: '22px',
                    boxSizing: 'border-box',
                    borderRadius: '2px',
                    border: `1px solid ${accent}`,
                    background: 'rgba(0,0,0,0.35)',
                    color: C.text,
                    fontFamily: 'PrimaryFont, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
                    fontSize: '11px',
                    letterSpacing: '0.02em',
                    padding: '0 10px',
                    outline: 'none',
                  }}
                />
                <button
                  type="button"
                  aria-label="Delete hashtag"
                  onMouseDown={(e) => {
                    // Prevent the focused input from blurring before click fires.
                    // Without this, blur runs commitTags(old list) first, the in-flight
                    // guard then blocks the delete's own commitTags call, so the deletion
                    // never actually saves.
                    e.preventDefault();
                  }}
                  onClick={() => {
                    const next = draftTags.filter((_, i) => i !== idx);
                    setDraftTags(next.length ? next : ['']);
                    commitTags(next.length ? next : ['']);
                  }}
                  style={{
                    width: '22px',
                    height: '22px',
                    flexShrink: 0,
                    borderRadius: '2px',
                    border: 'none',
                    background: '#dc2626',
                    color: '#fee2e2',
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width="11" height="12" viewBox="-6 -6 12 12.5" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
                    <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
                    <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
                    <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                    <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </foreignObject>
      )}
    </g>
  );
}
